import React, { useEffect, useState, useMemo } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    VStack,
    HStack,
    Box,
    Image,
    Text,
    Spinner,
    Button,
    Badge,
    Flex,
    Icon,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from "@chakra-ui/react";
import { collection, query, where, getDocs, doc } from "firebase/firestore";
import { db } from "../../firebase/firebase-config";
import { useNavigate } from "react-router-dom";
import {
    IoTicketOutline,
    IoCalendarOutline,
    IoLocationOutline,
    IoTimeOutline,
    IoArrowForwardOutline,
    IoSparklesOutline,
} from "react-icons/io5";

export const MyTicketsModal = ({ isOpen, onClose, user }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen && user) {
            fetchUserTickets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, user]);

    const fetchUserTickets = async () => {
        setLoading(true);
        try {
            const userRef = doc(db, "users", user.uid);
            const eventsRef = collection(db, "events");
            const q = query(
                eventsRef,
                where("members", "array-contains", userRef)
            );
            const snapshot = await getDocs(q);

            const ticketsList = snapshot.docs
                .map((d) => ({ docId: d.id, ...d.data() }))
                // Only tickets for events the user is attending (not hosting).
                .filter((event) => event.host !== user.uid)
                .sort((a, b) => {
                    const dateA =
                        a.startTime?.seconds
                            ? new Date(a.startTime.seconds * 1000)
                            : new Date(0);
                    const dateB =
                        b.startTime?.seconds
                            ? new Date(b.startTime.seconds * 1000)
                            : new Date(0);
                    return dateA - dateB;
                });

            setTickets(ticketsList);
        } catch (error) {
            console.error("Error fetching tickets:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewEvent = (event) => {
        onClose();
        const eventId = event.id || event.docId;
        const shortId = eventId.replace(/[\[\]#]/g, "");
        navigate(`/hangout/${shortId}`);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "No date";
        const date = timestamp.seconds
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp);
        return date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
        });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.seconds
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp);
        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    const isUpcoming = (timestamp) => {
        if (!timestamp) return false;
        const date = timestamp.seconds
            ? new Date(timestamp.seconds * 1000)
            : new Date(timestamp);
        return date > new Date();
    };

    const { upcoming, past } = useMemo(() => {
        const up = [];
        const pa = [];
        tickets.forEach((t) => {
            if (isUpcoming(t.startTime)) up.push(t);
            else pa.push(t);
        });
        // Past tickets are shown most-recent first.
        pa.reverse();
        return { upcoming: up, past: pa };
    }, [tickets]);

    const TicketCard = ({ event }) => {
        const upcoming = isUpcoming(event.startTime);
        return (
            <Flex
                role="group"
                direction="row"
                bg="white"
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="xl"
                overflow="hidden"
                cursor="pointer"
                transition="all 0.2s ease"
                onClick={() => handleViewEvent(event)}
                _hover={{
                    borderColor: "#508cf7",
                    boxShadow: "0 10px 24px -12px rgba(80,140,247,0.35)",
                    transform: "translateY(-1px)",
                }}
                minW={0}
            >
                <Box
                    position="relative"
                    boxSize={{ base: "96px", sm: "110px" }}
                    flexShrink={0}
                    bg="gray.100"
                    alignSelf="center"
                    m={2}
                    borderRadius="lg"
                    overflow="hidden"
                >
                    <Image
                        src={
                            event.imageUrl ||
                            "https://via.placeholder.com/200x200?text=Event"
                        }
                        alt={event.name}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                    />
                </Box>

                <Box p={3} flex={1} minW={0}>
                    <HStack
                        justify="space-between"
                        align="flex-start"
                        spacing={2}
                        mb={1}
                    >
                        <Text
                            fontWeight="700"
                            fontSize="sm"
                            color="gray.800"
                            noOfLines={2}
                            lineHeight="1.3"
                            flex={1}
                            minW={0}
                        >
                            {event.name}
                        </Text>
                        <Badge
                            fontSize="10px"
                            fontWeight="700"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            textTransform="uppercase"
                            letterSpacing="0.05em"
                            bg={upcoming ? "green.50" : "gray.100"}
                            color={upcoming ? "green.600" : "gray.500"}
                            border="1px solid"
                            borderColor={upcoming ? "green.100" : "gray.200"}
                            flexShrink={0}
                        >
                            {upcoming ? "Upcoming" : "Past"}
                        </Badge>
                    </HStack>

                    <HStack spacing={3} color="gray.500" fontSize="xs" mt={1}>
                        <HStack spacing={1}>
                            <Icon as={IoCalendarOutline} boxSize="12px" />
                            <Text fontWeight="600">
                                {formatDate(event.startTime)}
                            </Text>
                        </HStack>
                        <HStack spacing={1}>
                            <Icon as={IoTimeOutline} boxSize="12px" />
                            <Text>{formatTime(event.startTime)}</Text>
                        </HStack>
                    </HStack>

                    {event.location && (
                        <HStack spacing={1} color="gray.500" fontSize="xs" mt={1}>
                            <Icon as={IoLocationOutline} boxSize="12px" />
                            <Text noOfLines={1}>
                                {event.location.split(",")[0]}
                            </Text>
                        </HStack>
                    )}
                </Box>
            </Flex>
        );
    };

    const EmptyState = ({ label }) => (
        <Flex
            direction="column"
            align="flex-start"
            textAlign="left"
            py={10}
            px={1}
            gap={3}
        >
            <Flex
                w="48px"
                h="48px"
                borderRadius="xl"
                bg="blue.50"
                align="center"
                justify="center"
            >
                <Icon as={IoTicketOutline} color="#508cf7" boxSize="22px" />
            </Flex>
            <Box>
                <Text fontSize="md" fontWeight="700" color="gray.800">
                    {label}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={0.5}>
                    When you register for events, your tickets show up here.
                </Text>
            </Box>
            {/* <Button
                size="sm"
                bg="#508cf7"
                color="white"
                rightIcon={<Icon as={IoArrowForwardOutline} />}
                _hover={{ bg: "#226cf1" }}
                onClick={onClose}
            >
                Browse events
            </Button> */}
        </Flex>
    );

    const renderList = (list, emptyLabel) => {
        if (list.length === 0) return <EmptyState label={emptyLabel} />;
        return (
            <VStack spacing={3} align="stretch">
                {list.map((event) => (
                    <TicketCard
                        key={event.docId || event.id}
                        event={event}
                    />
                ))}
            </VStack>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            size={{ base: "full", md: "md" }}
            scrollBehavior="inside"
        >
            <ModalOverlay
                backdropFilter="blur(4px)"
                bg="blackAlpha.500"
            />
            <ModalContent
                borderRadius={{ base: 0, md: "2xl" }}
                overflow="hidden"
            >
                <ModalHeader
                    pb={2}
                    pt={5}
                    px={6}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                >
                    <HStack spacing={3} align="center">
                        <Box
                            bg="blue.50"
                            p={2}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon
                                as={IoTicketOutline}
                                color="#508cf7"
                                boxSize={4}
                            />
                        </Box>
                        <Box>
                            <HStack spacing={1.5}>
                                <Text
                                    className="font-gilroy-bold"
                                    fontSize="lg"
                                    fontWeight="700"
                                    color="gray.800"
                                >
                                    My Tickets
                                </Text>
                                <Icon as={IoSparklesOutline} color="#508cf7" boxSize="13px" />
                            </HStack>
                            <Text fontSize="xs" color="gray.400" fontWeight="500">
                                Everything you've got lined up
                            </Text>
                        </Box>
                    </HStack>
                    <ModalCloseButton top={4} right={4} />
                </ModalHeader>

                <ModalBody px={6} py={5} bg="white">
                    {loading ? (
                        <Flex justify="center" align="center" py={16}>
                            <Spinner
                                size="lg"
                                color="#508cf7"
                                thickness="3px"
                            />
                        </Flex>
                    ) : tickets.length === 0 ? (
                        <Box>
                            <EmptyState label="No tickets yet" />
                        </Box>
                    ) : (
                        <Tabs
                            variant="unstyled"
                            colorScheme="blue"
                            isLazy
                        >
                            <TabList
                                px={0}
                                pt={0}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                                gap={2}
                            >
                                {[
                                    { label: "Upcoming", count: upcoming.length },
                                    { label: "Past", count: past.length },
                                ].map((t) => (
                                    <Tab
                                        key={t.label}
                                        fontSize="sm"
                                        fontWeight="600"
                                        color="gray.500"
                                        px={0}
                                        pb={3}
                                        mr={5}
                                        borderBottom="2px solid"
                                        borderColor="transparent"
                                        _selected={{
                                            color: "#508cf7",
                                            borderColor: "#508cf7",
                                        }}
                                        _hover={{ color: "#226cf1" }}
                                    >
                                        <HStack spacing={2}>
                                            <Text>{t.label}</Text>
                                            <Badge
                                                bg="gray.100"
                                                color="gray.600"
                                                fontSize="10px"
                                                borderRadius="full"
                                                px={2}
                                                py={0.5}
                                            >
                                                {t.count}
                                            </Badge>
                                        </HStack>
                                    </Tab>
                                ))}
                            </TabList>

                            <TabPanels>
                                <TabPanel px={0} py={5} pb={0}>
                                    {renderList(upcoming, "No upcoming tickets")}
                                </TabPanel>
                                <TabPanel px={0} py={5} pb={0}>
                                    {renderList(past, "Nothing in the history yet")}
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    )}
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="gray.100" py={4} px={6}>
                    <Button
                        size="sm"
                        variant="ghost"
                        fontWeight="600"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
